/**
 * @module public/js/utils/userPhoneNumber
 * @summary Shared seller/user phone normalization for profile and admin forms.
 */

/**
 * Normalize user-entered routing phone input into safe E.164.
 *
 * Accepts blank values, US 10-digit numbers, 11-digit NANP numbers prefixed
 * with `1`, and international numbers that already include a leading `+`.
 * Inputs containing letters, extensions, or malformed plus placement are
 * rejected instead of guessed to avoid misrouting calls.
 *
 * @param {unknown} value - Raw phone input from the browser.
 * @returns {string} Canonical E.164 number or an empty string when cleared.
 * @throws {Error} When the value is not blank and cannot be safely normalized.
 */
export function normalizeUserPhoneNumber(value) {
  const raw = String(value ?? '').trim();
  if (!raw) {
    return '';
  }

  if (/[^0-9+\s().-]/.test(raw)) {
    throw new Error('Enter a valid US or E.164 phone number.');
  }

  const plusMatches = raw.match(/\+/g) || [];
  if (
    plusMatches.length > 1 ||
    (plusMatches.length === 1 && !raw.startsWith('+'))
  ) {
    throw new Error('Enter a valid US or E.164 phone number.');
  }

  const digits = raw.replace(/\D+/g, '');
  if (!digits) {
    throw new Error('Enter a valid US or E.164 phone number.');
  }

  if (raw.startsWith('+')) {
    const normalized = `+${digits}`;
    if (/^\+[1-9]\d{9,14}$/.test(normalized)) {
      return normalized;
    }
    throw new Error('Enter a valid US or E.164 phone number.');
  }

  if (/^\d{10}$/.test(digits)) {
    return `+1${digits}`;
  }

  if (/^1\d{10}$/.test(digits)) {
    return `+${digits}`;
  }

  throw new Error('Enter a valid US or E.164 phone number.');
}
